import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Form from "@rjsf/core";
import {
  RegistryFieldsType,
  RegistryWidgetsType,
  RJSFValidationError,
} from "@rjsf/utils";
import { flatMap, isArray, isEqual, isObject } from "lodash";
import { useForm, useWatch } from "react-hook-form";
import { SubmissionDefaultValuesContextV2 } from "common/services/formBuilderService";
import { extractGenerativeAIFields } from "common/services/submissionHelpers";
import { BuildSISDRuleParams } from "common/utils/improvements";
import {
  applyConditionalRules,
  buildContext,
} from "common/utils/submissions/conditionalRules";
import { SearchResultProps } from "common-client/utils/search";
import { transformError } from "common-client/utils/submissions";
import { customValidator } from "common-client/utils/submissions/customValidator";
import { useResolvedFormData } from "common-client/utils/useResolvedFormData";
import {
  CertificateUpload,
  DocumentUpload,
  GetSisdRuleDataQuery,
  Submission,
  SubmissionType,
  SubmissionTypeVersion,
} from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import Disclaimer from "../Common/Disclaimer";
import Wrapper from "../Inputs/Wrapper";
import { OldOrNewSearch } from "../Search";
import { SearchContextProvider } from "../Search/SearchContext";
import {
  form,
  InputsWrapper,
  PropertySearchInputWrapper,
} from "./__styles__/Form";
import { Stack } from "./__styles__/Submission";
import {
  DocumentUploadInput,
  MultiSelectInput,
  NumberInput,
  NumberWithUnitsInput,
  PropertyMarketValueInput,
  TagsInput,
  UniqueIdInput,
  UserInput,
} from "./Fields";
import { DocumentUpload as DocumentUploadFieldType } from "./Fields/DocumentUploadField";
import { MODULE_CONFIGURATIONS } from "./modules";
import {
  DescriptionFieldTemplate,
  ObjectFieldTemplate,
  TitleFieldTemplate,
} from "./Templates";
import {
  CustomCheckbox,
  CustomDatePicker,
  CustomSelect,
  CustomText,
  CustomTextarea,
  DamageDegree,
} from "./Widgets";

export type FormDataType = Record<string, unknown>;
export type LoadingFormPath = Record<string, boolean>;

interface SearchBoxFormFields {
  objectLabel: string;
}

export interface SubmissionFormContextType {
  documentUploads: Maybe<
    Array<
      Pick<
        DocumentUpload,
        "id" | "originalFilename" | "hiddenFromPublic" | "externalDocumentURL"
      > & {
        certificateUpload?: Maybe<
          Pick<CertificateUpload, "status" | "cancelationReason">
        >;
      }
    >
  >;
  defaultValues: SubmissionDefaultValuesContextV2;
  editing: boolean;
  regenerateAIFields: ((paths: string[]) => void) | null;
  loadingFormPaths: LoadingFormPath;
}

export const SubmissionFormContext =
  React.createContext<SubmissionFormContextType>({
    documentUploads: null,
    defaultValues: {} as SubmissionDefaultValuesContextV2,
    editing: false,
    regenerateAIFields: null,
    loadingFormPaths: {},
  });

export type RelatedSubmission = Pick<Submission, "id"> & {
  submissionTypeVersion: {
    submissionType: Pick<SubmissionType, "modules" | "name">;
  };
};

export interface FormComponentProps {
  submitFormRef?: React.RefObject<HTMLButtonElement>;
  submissionType: {
    id: string;
    modules: SubmissionType["modules"];
    currentVersion: Pick<
      SubmissionTypeVersion,
      "formStructure" | "conditionalRules"
    >;
  };
  existingSubmission?: Maybe<
    Pick<Submission, "formData"> & {
      documentUploads: Array<
        Pick<DocumentUpload, "id" | "originalFilename" | "hiddenFromPublic">
      >;
    }
  >;
  disabled?: boolean;
  readonly?: boolean;
  onSubmit: (formData: FormDataType) => void;
  regenerateAIFields?:
    | ((formData: FormDataType, paths: string[]) => void)
    | null;
  onChange?: (data: {
    isDirty: boolean;
    documentUploadsInProgress: boolean;
    searchAddressCleared?: boolean;
  }) => void;
  dynamicDefaultValues: SubmissionDefaultValuesContextV2;
  initialFormData?: FormDataType;
  serverProvidedFormData?: FormDataType;
  relatedSubmissions: RelatedSubmission[];
  account: GetSisdRuleDataQuery["account"];
  property: {
    FIRMInfo?: GetSisdRuleDataQuery["FIRMInfo"];
    fullAddress?: string;
  };
  loadingFormPaths: LoadingFormPath;
  onSearchResultClick?: (result: SearchResultProps) => void;
  onSearchClear?: () => void;
  loadingSearchValue?: boolean;
}

const isDocumentUploadField = (field: any) => {
  return isArray(field) && field.length && !!field[0].accountDocumentTypeId;
};

const widgets: RegistryWidgetsType = {
  SelectWidget: CustomSelect,
  TextareaWidget: CustomTextarea,
  DateWidget: CustomDatePicker,
  DamageDegree: DamageDegree,
  TextWidget: CustomText,
  CheckboxWidget: CustomCheckbox,
};

const fields: RegistryFieldsType = {
  DocumentUploader: DocumentUploadInput,
  multiSelect: MultiSelectInput,
  Tags: TagsInput,
  NumberField: NumberInput,
  NumberWithUnits: NumberWithUnitsInput,
  PropertyMarketValue: PropertyMarketValueInput,
  User: UserInput,
  UniqueId: UniqueIdInput,
  // Hidden field that renders nothing - used by conditional rules
  // See RJSF documentation for hidden widgets https://rjsf-team.github.io/react-jsonschema-form/docs/usage/widgets/#hidden-widgets
  hidden: () => null,
};

export const FormComponent = ({
  submitFormRef,
  submissionType,
  existingSubmission,
  relatedSubmissions,
  initialFormData,
  serverProvidedFormData,
  disabled = false,
  readonly = false,
  onSubmit,
  onChange = () => {},
  dynamicDefaultValues,
  account,
  property,
  regenerateAIFields,
  loadingFormPaths,
  onSearchResultClick,
  onSearchClear,
  loadingSearchValue = false,
}: FormComponentProps) => {
  const {
    control: searchControl,
    setValue: setSearchValue,
    formState: { errors: addressErrors },
  } = useForm<SearchBoxFormFields>({
    defaultValues: {
      objectLabel: property?.fullAddress || "",
    },
  });

  const searchValue = useWatch({ control: searchControl, name: "objectLabel" });

  // Sync address value when property.fullAddress changes
  useEffect(() => {
    if (property?.fullAddress) {
      setSearchValue("objectLabel", property.fullAddress);
    }
  }, [property?.fullAddress, setSearchValue]);

  // Preserve the original form data for use with submission gates
  const originalFormData = useMemo(() => {
    if (existingSubmission?.formData) {
      return JSON.parse(JSON.stringify(existingSubmission.formData));
    }

    return {};
  }, [existingSubmission?.formData]);

  const finalInitialFormData = useMemo(() => {
    if (initialFormData) {
      return initialFormData;
    }

    if (existingSubmission?.formData) {
      return JSON.parse(JSON.stringify(existingSubmission.formData));
    }

    return {};
  }, [initialFormData, existingSubmission?.formData]);

  const { account: contextAccount, admin } = useContext(AuthContext);

  const { modules, currentVersion } = submissionType;
  const { schema, uiSchema } = currentVersion.formStructure;

  const checkDocumentUploadsInProgress = useCallback((data: any): boolean => {
    for (const key of Object.keys(data)) {
      if (isObject(data[key])) {
        const subValue = checkDocumentUploadsInProgress(data[key]);
        if (subValue) {
          return true;
        }
      }

      if (isDocumentUploadField(data[key])) {
        const validUpload = data[key].every(
          (item: DocumentUploadFieldType) =>
            !item.status || item.status === "valid",
        );

        if (!validUpload) {
          return true;
        }
      }
    }
    return false;
  }, []);

  const computeFormValues = useCallback(
    (fieldValues: FormDataType) => {
      return modules.reduce(
        (formData, module) => {
          return MODULE_CONFIGURATIONS[module].onFormDataChange(formData, {
            rules:
              account?.sisdRuleDefinition[0]?.rules.filter(
                (rule): rule is BuildSISDRuleParams =>
                  rule.__typename === "SISDRule",
              ) ?? [],
            firms: (property.FIRMInfo?.firms ?? []).map(firm => ({
              sfha: firm.sfha,
              jurisdictions: firm.jurisdictions,
              depth: firm.depth ?? null,
              stringDepth: firm.stringDepth ?? null,
              staticBFE: firm.staticBFE ?? null,
              stringStaticBFE: firm.stringStaticBFE ?? null,
              floodzone: firm.floodzone,
            })),
          });
        },
        { ...fieldValues },
      );
    },
    [modules, account, property],
  );

  const handleFormChange = useCallback(
    (formData: FormDataType) => {
      const isInProgress = checkDocumentUploadsInProgress(formData);

      onChange({
        isDirty: !isEqual(existingSubmission?.formData, formData),
        documentUploadsInProgress: isInProgress,
      });
    },
    [checkDocumentUploadsInProgress, existingSubmission?.formData, onChange],
  );

  const { resolvedFormData, onFormChange, updateFormData } =
    useResolvedFormData({
      computeFormValues,
      onChange: handleFormChange,
      dynamicDefaultValues,
      initialFormData: finalInitialFormData,
      serverProvidedFormData,
      formStructure: currentVersion.formStructure,
    });

  const { schema: modifiedSchema, uiSchema: modifiedUiSchema } = useMemo(() => {
    return applyConditionalRules({
      schema,
      uiSchema,
      rules: currentVersion.conditionalRules,
      context: buildContext(submissionType.id, resolvedFormData),
    });
  }, [
    schema,
    uiSchema,
    resolvedFormData,
    currentVersion.conditionalRules,
    submissionType.id,
  ]);

  const [validated, setValidated] = useState(false);

  const editing = !!existingSubmission;

  const handleRegenerateAIFields =
    regenerateAIFields ?
      (paths: string[]) => {
        regenerateAIFields(resolvedFormData, paths);
      }
    : null;

  const handleSubmit = ({
    formData,
  }: {
    formData: FormDataType;
    previousFormData: FormDataType;
  }) => {
    updateFormData(formData);
    onSubmit(formData);
  };

  const onError = (formErrors: Array<RJSFValidationError>) => {
    // onSubmit isn't called if there are errors, so we have to set validated state here. We only want to
    // live validate after the initial submit click, so we don't show a bunch of errors on page load
    setValidated(true);
    const firstErrorElement = formErrors
      .map(error => {
        const replacedName = error.property?.replace(/\./g, "_");
        // For reasons that are unknown to me, sometimes properties don't start with a `.`,
        // so we are checking if a leading `_` is present before prepending `root`
        const rootName =
          replacedName?.startsWith("_") ?
            `root${replacedName}`
          : `root_${replacedName}`;

        return document.getElementById(rootName);
      })
      .filter(element => element !== null)
      .sort((a, b) => {
        const aRect = a.getBoundingClientRect();
        const bRect = b.getBoundingClientRect();
        return aRect.top - bRect.top || aRect.left - bRect.left;
      })[0];

    // Some inputs, such as DocumentUploadField, don't receive focus,
    // so we want to scroll and then focus
    firstErrorElement?.scrollIntoView({ block: "center" });
    firstErrorElement?.focus();
  };

  const disclaimers = flatMap(modules, module => {
    return MODULE_CONFIGURATIONS[module].buildDisclaimers({
      relatedSubmissions,
      user: { isAdmin: !!admin, accountId: contextAccount!.id },
    });
  });

  const genAIFields = extractGenerativeAIFields({
    formStructure: currentVersion.formStructure,
  });

  const regeneratingPaths: LoadingFormPath = {};
  genAIFields.map(field => {
    regeneratingPaths[field.path] = loadingFormPaths[field.path] ?? false;
  });
  if (genAIFields.length > 0) {
    disclaimers.push(
      "This form contains fields that may be automatically generated by AI. While every effort is made to ensure accuracy and relevance, please review all generated content carefully after initial save. If any information appears incorrect or unclear, feel free to edit the field directly or regenerate the content.",
    );
  }

  const submissionGates = modules.reduce((chain, module) => {
    const gate = MODULE_CONFIGURATIONS[module].buildSubmitGate();
    return ({
      formData,
      previousFormData,
    }: {
      formData: FormDataType;
      previousFormData: FormDataType;
    }) => {
      return gate({
        formData,
        previousFormData,
        relatedSubmissions,
        onComplete: chain,
      });
    };
  }, handleSubmit);

  return (
    <SubmissionFormContext.Provider
      value={{
        editing,
        documentUploads: existingSubmission?.documentUploads ?? null,
        defaultValues: dynamicDefaultValues,
        regenerateAIFields: handleRegenerateAIFields,
        loadingFormPaths: regeneratingPaths,
      }}
    >
      <Stack>
        {disclaimers.map((disclaimer, index) => (
          <Disclaimer key={index} message={disclaimer} margin={false} />
        ))}
        {/* TODO: Remove InputsWrapper and PropertySearchInputWrapper once */}
        {/* individual form fields and inputs no longer set their own margin/padding */}
        <InputsWrapper>
          {onSearchResultClick && (
            <PropertySearchInputWrapper>
              <Wrapper
                name="objectLabel"
                label="Address"
                error={addressErrors.objectLabel?.message}
                required
                printValue={searchValue}
              >
                <SearchContextProvider>
                  <OldOrNewSearch
                    // eslint-disable-next-line no-restricted-syntax
                    handleSearchResultClick={onSearchResultClick}
                    handleSearchClear={onSearchClear}
                    control={searchControl}
                    name="objectLabel"
                    rules={{
                      required: "Address is required",
                    }}
                    outline
                    compactSearchPanel
                    overlaySearchPanel
                    addressOnly
                    originalValue={property?.fullAddress || undefined}
                    loadingOriginalValue={loadingSearchValue}
                  />
                </SearchContextProvider>
              </Wrapper>
            </PropertySearchInputWrapper>
          )}
          <Form
            disabled={disabled}
            readonly={readonly}
            schema={modifiedSchema}
            uiSchema={modifiedUiSchema}
            validator={customValidator}
            widgets={widgets}
            fields={fields}
            onSubmit={() =>
              submissionGates({
                formData: resolvedFormData,
                previousFormData: originalFormData,
              })
            }
            className={form()}
            formData={resolvedFormData}
            noHtml5Validate={false}
            transformErrors={errors => {
              return errors.map(error => ({
                ...error,
                ...transformError(error),
              }));
            }}
            showErrorList={false}
            // We only want to liveValidate after initial form submission
            liveValidate={validated}
            onError={onError}
            onChange={onFormChange}
            templates={{
              TitleFieldTemplate,
              DescriptionFieldTemplate,
              ObjectFieldTemplate,
            }}
          >
            {submitFormRef && (
              <button
                ref={submitFormRef}
                type="submit"
                style={{ display: "none" }}
              />
            )}
          </Form>
        </InputsWrapper>
      </Stack>
    </SubmissionFormContext.Provider>
  );
};
